import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';

import { useReplyDraftsGateway } from './ReplyDraftsGatewayProvider';
import { toReplyDraftError } from '../api/errors';
import { getReplyDraftV2QueryOptions } from '../api/queryOptions';
import { isUnusableDraftError, ReplyDraftError, type ReplyDraftErrorKind } from '../model/errors';
import { OPEN_REPLY_DRAFTS_QUERY_KEY } from '../model/queryKeys';
import type { CheckItem, MustFixAcknowledgement, ReplyDraft } from '../model/types';

import { THIRTY_SECONDS } from '@/constants/time';
import { trackReplyDraftUseFailed } from '@/util/analyticsEvents';
import { Sentry } from '@/util/sentry';

/**
 * How long "use draft" may take. The server runs a model check of the reply
 * before handing it off, measured at about nine seconds typically and twelve
 * at most, so this leaves room without leaving the expert waiting forever.
 */
export const USE_DRAFT_TIMEOUT_MS = THIRTY_SECONDS;

export type UseDraftPhase =
  | { kind: 'idle' }
  | { kind: 'checking' }
  | { kind: 'must_fix'; items: CheckItem[]; composedHash: string }
  | { kind: 'failed'; error: ReplyDraftErrorKind };

const IDLE: UseDraftPhase = { kind: 'idle' };

const REPORTED = new Set<ReplyDraftErrorKind>(['network', 'timeout', 'unknown', 'invalid_status']);

export type UseDraftCallbacks = {
  /** Sends every unsaved tap first, so the server composes what is on screen. */
  flush: () => Promise<boolean>;
  onHandedOff: (composedText: string) => void;
  onConflict?: () => void;
  onUnusable?: (error: ReplyDraftError) => void;
};

/**
 * "Use draft": asks the server to compose the reply from the recorded state
 * and hand it over. The app never sends text it composed itself.
 *
 * - The unsaved taps go first, then the request, with the latest version.
 * - Only one request runs at a time, so a second tap during the check does
 *   nothing.
 * - The wait is capped at `USE_DRAFT_TIMEOUT_MS`. The cap is a race against
 *   the request as well as an abort of it, so the expert is never left
 *   waiting on a transport that ignores the abort.
 * - A must-fix hold becomes the `must_fix` phase with the items and the hash
 *   of the text they were checked on. `submit` is called again with the
 *   acknowledgement; dismissing the list returns to idle and hands off nothing.
 * - After a hand-off the draft is re-read, because the server moved it to
 *   `handed_off` and bumped its version, and the next edit must carry the new one.
 */
export function useDraftHandOff(replyDraftId: string, callbacks: UseDraftCallbacks) {
  const gateway = useReplyDraftsGateway();

  const queryClient = useQueryClient();

  const [phase, setPhase] = useState<UseDraftPhase>(IDLE);

  const inFlightRef = useRef(false);

  const callbacksRef = useRef(callbacks);

  useLayoutEffect(() => {
    callbacksRef.current = callbacks;
  });

  const submit = useCallback(
    async (acknowledgement?: MustFixAcknowledgement) => {
      if (inFlightRef.current) {
        return;
      }

      inFlightRef.current = true;

      setPhase({ kind: 'checking' });

      const { queryKey } = getReplyDraftV2QueryOptions(replyDraftId, gateway);

      let timer: ReturnType<typeof setTimeout> | undefined;

      try {
        if (!(await callbacksRef.current.flush())) {
          setPhase(IDLE);

          return;
        }

        const server = queryClient.getQueryData<ReplyDraft>(queryKey);

        if (!server) {
          setPhase(IDLE);

          return;
        }

        const controller = new AbortController();

        const timeout = new Promise<never>((_resolve, reject) => {
          timer = setTimeout(() => {
            controller.abort();

            reject(new ReplyDraftError('timeout', 'Use draft timed out'));
          }, USE_DRAFT_TIMEOUT_MS);
        });

        const { composedText } = await Promise.race([
          gateway.useDraft(replyDraftId, server.stateVersion, acknowledgement, controller.signal),
          timeout,
        ]);

        queryClient.invalidateQueries({ queryKey }).catch(() => undefined);

        queryClient
          .invalidateQueries({ queryKey: OPEN_REPLY_DRAFTS_QUERY_KEY })
          .catch(() => undefined);

        setPhase(IDLE);

        callbacksRef.current.onHandedOff(composedText);
      } catch (thrown) {
        const error = toReplyDraftError(thrown);

        if (error.kind === 'must_fix' && error.mustFix) {
          setPhase({ kind: 'must_fix', ...error.mustFix });

          return;
        }

        if (error.kind === 'stale_state' && error.current) {
          queryClient.setQueryData(queryKey, error.current);

          setPhase(IDLE);

          callbacksRef.current.onConflict?.();

          return;
        }

        trackReplyDraftUseFailed({ replyDraftId, kind: error.kind });

        if (isUnusableDraftError(error)) {
          setPhase(IDLE);

          callbacksRef.current.onUnusable?.(error);

          return;
        }

        if (REPORTED.has(error.kind)) {
          Sentry.captureException(error, {
            tags: { component: 'useDraftHandOff', source: 'reply-draft' },
            extra: { errorMessage: error.message, kind: error.kind, replyDraftId },
          });
        }

        setPhase({ kind: 'failed', error: error.kind });
      } finally {
        clearTimeout(timer);

        inFlightRef.current = false;
      }
    },
    [replyDraftId, gateway, queryClient]
  );

  const dismiss = useCallback(() => setPhase(IDLE), []);

  return { phase, submit, dismiss };
}
